import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import RegisterLink from '../../../components/RegisterLink';
import { useLanguage } from '../../../contexts/LanguageContext';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { lang, setLang, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: t('Home', 'Utama'), href: '/' },
    { label: t('Bonus & Promotions', 'Bonus & Promosi'), href: '/bonus/' },
    { label: t('Hot Games', 'Permainan Terpantas'), href: '/games/' },
    { label: t('About MrCuci', 'Tentang MrCuci'), href: '/about/' },
    { label: t('FAQ', 'FAQ'), href: '/faq/' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? 'bg-[#0d0000] backdrop-blur-md shadow-[0_2px_20px_rgba(204,0,0,0.2)]'
          : 'bg-transparent'
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 md:px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center flex-shrink-0">
          <img
            src="https://static.readdy.ai/image/b513b5b4a847779ee65de1525456761b/0922d8cb6b39a31ad70c8c729d5dd75a.png"
            alt="MrCuci Logo"
            className="h-10 md:h-12 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav Links */}
        <ul className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <li key={link.href}>
              <Link
                to={link.href}
                className="px-3 py-2 text-base text-gray-300 hover:text-[#d4a017] transition-colors duration-200 whitespace-nowrap cursor-pointer font-medium tracking-wide"
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>

        {/* Right: Language + Auth */}
        <div className="hidden lg:flex items-center gap-2">
          {/* Language Toggle */}
          <div className="flex items-center bg-[#1a0303]/80 border border-[#d4a017]/20 rounded-full p-1">
            <button
              onClick={() => setLang('en')}
              className={`px-3 py-1 text-xs font-bold rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                lang === 'en'
                  ? 'bg-gradient-to-r from-[#d4a017] to-[#f5c518] text-black'
                  : 'text-gray-400 hover:text-[#d4a017]'
              }`}
            >
              EN
            </button>
            <button
              onClick={() => setLang('bm')}
              className={`px-3 py-1 text-xs font-bold rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                lang === 'bm'
                  ? 'bg-gradient-to-r from-[#d4a017] to-[#f5c518] text-black'
                  : 'text-gray-400 hover:text-[#d4a017]'
              }`}
            >
              BM
            </button>
          </div>
          <RegisterLink
            source="navbar_desktop_register"
            label={t('Register Now', 'Daftar Sekarang')}
            className="px-4 py-2 text-sm font-semibold bg-gradient-to-r from-[#d4a017] to-[#f5c518] text-black rounded-md hover:opacity-90 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-[0_0_12px_rgba(212,160,23,0.4)]"
          >
            {t('Register Now', 'Daftar Sekarang')}
          </RegisterLink>
        </div>

        {/* Mobile Right Side */}
        <div className="lg:hidden flex items-center gap-2">
          {/* Mobile Language Toggle */}
          <div className="flex items-center bg-[#1a0303]/80 border border-[#d4a017]/20 rounded-full p-0.5">
            <button
              onClick={() => setLang('en')}
              className={`px-2 py-0.5 text-xs font-bold rounded-full transition-all whitespace-nowrap cursor-pointer ${
                lang === 'en' ? 'bg-[#d4a017] text-black' : 'text-gray-400'
              }`}
            >
              EN
            </button>
            <button
              onClick={() => setLang('bm')}
              className={`px-2 py-0.5 text-xs font-bold rounded-full transition-all whitespace-nowrap cursor-pointer ${
                lang === 'bm' ? 'bg-[#d4a017] text-black' : 'text-gray-400'
              }`}
            >
              BM
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="flex flex-col gap-1.5 p-2 cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`block w-6 h-0.5 bg-[#d4a017] transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block w-6 h-0.5 bg-[#d4a017] transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`block w-6 h-0.5 bg-[#d4a017] transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden bg-[#0d0000] border-t border-[#d4a017]/20 px-4 py-4">
          <ul className="flex flex-col gap-1 mb-4">
            {navLinks.map((link) => (
              <li key={link.href}>
                <Link
                  to={link.href}
                  className="block py-2.5 px-3 text-sm text-gray-300 hover:text-[#d4a017] hover:bg-[#d4a017]/5 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
          <div className="flex gap-3 pt-3 border-t border-[#d4a017]/10">
            <RegisterLink
              source="navbar_mobile_login"
              label={t('Login', 'Log Masuk')}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex-1 text-center py-2.5 text-sm font-semibold text-[#d4a017] border border-[#d4a017]/50 rounded-lg whitespace-nowrap cursor-pointer hover:bg-[#d4a017]/10 transition-colors"
            >
              {t('Login', 'Log Masuk')}
            </RegisterLink>
            <RegisterLink
              source="navbar_mobile_register"
              label={t('Register', 'Daftar')}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex-1 text-center py-2.5 text-sm font-semibold bg-gradient-to-r from-[#d4a017] to-[#f5c518] text-black rounded-lg whitespace-nowrap cursor-pointer shadow-[0_0_10px_rgba(212,160,23,0.3)]"
            >
              {t('Register', 'Daftar')}
            </RegisterLink>
          </div>
        </div>
      )}
    </nav>
  );
}
